import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Broadcast, useBroadcasts } from './useNotifications'

interface UseAnnouncementModalReturn {
  isOpen: boolean
  unreadAnnouncements: Broadcast[]
  totalCount: number
  close: () => void
  markAsRead: () => void
  refresh: () => void
}

export function useAnnouncementModal(
  userId: string | null | undefined,
  language: 'en' | 'zh' = 'en'
): UseAnnouncementModalReturn {
  const [isOpen, setIsOpen] = useState(false)
  const [hasAutoOpened, setHasAutoOpened] = useState(false)
  const queryClient = useQueryClient()

  const {
    activeModals,
    dismissBroadcast,
    trackBroadcastEvent,
  } = useBroadcasts(userId, language)

  const unreadAnnouncements = useMemo(() => {
    return activeModals.filter(b => b.display_type === 'modal')
  }, [activeModals])

  useEffect(() => {
    if (!hasAutoOpened && unreadAnnouncements.length > 0) {
      setIsOpen(true)
      setHasAutoOpened(true)
    }
  }, [unreadAnnouncements.length, hasAutoOpened])

  useEffect(() => {
    if (!isOpen || unreadAnnouncements.length === 0) return
    unreadAnnouncements.forEach(announcement => {
      trackBroadcastEvent(announcement.id, 'view', { source: 'announcement_modal' })
    })
  }, [isOpen, unreadAnnouncements, trackBroadcastEvent])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const markAsRead = useCallback(() => {
    unreadAnnouncements.forEach(announcement => {
      dismissBroadcast(announcement.id)
    })
    setIsOpen(false)
  }, [unreadAnnouncements, dismissBroadcast])

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['broadcasts', userId] })
  }, [queryClient, userId])

  return {
    isOpen,
    unreadAnnouncements,
    totalCount: unreadAnnouncements.length,
    close,
    markAsRead,
    refresh,
  }
}

export default useAnnouncementModal
